import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calculateSmsSegments } from '../../utils/smsSegments';

/**
 * Segment counter for SMS compose fields.
 *
 * The typed text is NOT the message: the backend wraps it in org name + tracking
 * URL (~90+ characters of fixed overhead), so this counts segments over
 * prefix + text + suffix, using the same GSM-7/UCS-2 algorithm as the backend
 * (shared test vectors in smsSegmentVectors.json keep the two from drifting).
 * With no typed text the backend sends `defaultMessage` instead — count that.
 */
function SmsSegmentCounter({ prefix = '', suffix = '', text = '', defaultMessage = '' }) {
  const { t } = useTranslation();

  const assembled = text ? `${prefix}${text}${suffix}` : defaultMessage;
  if (!assembled) {
    return null;
  }

  const info = calculateSmsSegments(assembled);
  const multiSegment = info.segmentCount > 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ color: multiSegment ? 'warning.main' : 'text.secondary' }}>
        {t('smsSegments.count', { count: info.segmentCount })}
        {' · '}
        {t('smsSegments.remaining', { count: Math.max(0, info.remainingInSegment) })}
      </Typography>
      {multiSegment && (
        <Tooltip title={t('smsSegments.multiSegmentHint')}>
          <Typography variant="caption" sx={{ color: 'warning.main', cursor: 'help' }}>
            {t('smsSegments.billedPerSegment')}
          </Typography>
        </Tooltip>
      )}
      {info.encoding === 'ucs2' && (
        <Tooltip title={t('smsSegments.ucs2Hint')}>
          <Typography variant="caption" sx={{ color: 'warning.main', cursor: 'help' }}>
            {t('smsSegments.ucs2')}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

export default SmsSegmentCounter;
